import { ReactNode } from 'react';
import { Stack } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ProtectedRoute } from '../ProtectedRoute';
import { UnauthenticatedHeader } from '../Header/UnauthenticatedHeader';
import { UnauthenticatedRemoteAppShell } from '../UnauthenticatedRemoteAppShell';
import { getUnauthenticatedRouteMatch } from '../../services/unauthenticatedRouteMatcher';
import type { AppRegistryCallbackContext } from '@opengov/psp-contract';

interface AuthGateProps {
  children: ReactNode;
}

/**
 * Top-level routing gate that sits between BrowserRouter and ProtectedRoute.
 *
 * Checks the current pathname against unauthenticated route patterns declared
 * in the app registry and route registry. If matched, renders the remote module
 * without auth context (no Layout, no sidebar, no entity). If not matched,
 * falls through to ProtectedRoute and the normal authenticated flow.
 */
export function AuthGate({ children }: AuthGateProps) {
  const { pathname } = useLocation();
  const match = getUnauthenticatedRouteMatch(pathname);

  if (match) {
    // Extract moduleConfig if the entry has one (app registry entries may have it)
    const moduleConfig =
      'moduleConfig' in match.entry && typeof match.entry.moduleConfig === 'function'
        ? (match.entry.moduleConfig as (
            ctx: AppRegistryCallbackContext
          ) => Record<string, unknown> | Promise<Record<string, unknown>>)
        : undefined;

    return (
      <Stack sx={{ height: '100vh' }}>
        <UnauthenticatedHeader />
        <Stack sx={{ flex: 1, minHeight: 0 }}>
          <UnauthenticatedRemoteAppShell
            basePath={match.entry.basePath}
            moduleRoute={match.matchedRoute}
            moduleConfig={moduleConfig}
          />
        </Stack>
      </Stack>
    );
  }

  return <ProtectedRoute>{children}</ProtectedRoute>;
}
